import { useTheme } from '../../context/ThemeContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import StarRating from '../StarRating.jsx';

export default function FontGrid({
  fonts, fontsLoading, fontsError, pagination, page, setPage,
  previewText, previewSize, setPreviewSize, viewMode,
  selectedFontId, setSelectedFontId, selectedFontIds, toggleSelectFont, selectAllVisible,
  compareMode, comparisonFontIds, toggleCompareFont,
  toggleFavorite, toggleActivation, handleDeleteFont, setActiveCount,
  refetchFonts, hasSelection, searchQuery,
}) {
  const { theme } = useTheme();
  const { user } = useAuth();

  return (
    <div style={{ padding: 24, flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: hasSelection ? 80 : 24, paddingRight: selectedFontId ? 384 : 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={fonts.length > 0 && selectedFontIds.size === fonts.length}
              onChange={selectAllVisible}
              style={{ accentColor: theme.accent, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, color: theme.textMuted }}>
              {hasSelection
                ? `${selectedFontIds.size} selected`
                : fontsLoading
                  ? 'Loading...'
                  : `${pagination.total || 0} font ${(pagination.total || 0) !== 1 ? 'families' : 'family'}`
              }
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: theme.textFaint }}>Preview size</span>
          <input
            type="range" min={16} max={72} value={previewSize}
            onChange={e => setPreviewSize(Number(e.target.value))}
            style={{ width: 80, accentColor: theme.accent }}
          />
        </div>
      </div>

      {fontsError && (
        <div style={{
          padding: '16px 20px', background: theme.redBg,
          border: `1px solid ${theme.redBorder}`, borderRadius: 10,
          color: theme.red, fontSize: 12, marginBottom: 16,
        }}>
          {fontsError}
          <button onClick={() => refetchFonts()} style={{
            marginLeft: 12, background: 'none', border: 'none',
            color: theme.red, textDecoration: 'underline', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 12,
          }}>Retry</button>
        </div>
      )}

      <div style={{
        display: viewMode === 'grid' ? 'grid' : 'flex',
        flexDirection: viewMode === 'list' ? 'column' : undefined,
        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(320px, 1fr))' : undefined,
        gap: viewMode === 'grid' ? 12 : 2,
      }}>
        {fonts.map(font => {
          const isSelected = selectedFontIds.has(font.id);
          const isComparing = comparisonFontIds.includes(font.id);
          return (
          <div
            key={font.id}
            data-font-id={String(font.id)}
            onClick={() => {
              if (compareMode) { toggleCompareFont(font.id); return; }
              setSelectedFontId(selectedFontId === font.id ? null : font.id);
            }}
            style={{
              background: isComparing ? theme.accentBg : isSelected ? theme.accentBg
                : selectedFontId === font.id ? theme.accentBg : theme.cardBg,
              border: isComparing ? `1px solid ${theme.accentBorder}` : isSelected ? `1px solid ${theme.accentBorder}`
                : selectedFontId === font.id ? `1px solid ${theme.accentBorder}` : `1px solid ${theme.cardBorder}`,
              borderRadius: 10,
              padding: viewMode === 'grid' ? '18px 20px' : '12px 20px',
              cursor: 'pointer', transition: 'all 0.15s ease',
              display: 'flex',
              flexDirection: viewMode === 'grid' ? 'column' : 'row',
              alignItems: viewMode === 'list' ? 'center' : undefined,
              gap: viewMode === 'list' ? 20 : 0,
              position: 'relative',
            }}
            onMouseEnter={e => {
              if (selectedFontId !== font.id && !isSelected && !isComparing) {
                e.currentTarget.style.background = theme.cardBgHover;
                e.currentTarget.style.borderColor = theme.cardBorderHover;
              }
              const cb = e.currentTarget.querySelector('.font-cb');
              if (cb) cb.style.opacity = '1';
            }}
            onMouseLeave={e => {
              if (selectedFontId !== font.id && !isSelected && !isComparing) {
                e.currentTarget.style.background = theme.cardBg;
                e.currentTarget.style.borderColor = theme.cardBorder;
              }
              const cb = e.currentTarget.querySelector('.font-cb');
              if (cb && !hasSelection) cb.style.opacity = '0';
            }}
          >
            {!compareMode && (
              <div className="font-cb" style={{
                position: 'absolute', top: 8, left: 8,
                opacity: hasSelection || isSelected ? 1 : 0,
                transition: 'opacity 0.15s',
              }}>
                <input
                  type="checkbox" checked={isSelected}
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleSelectFont(font.id)}
                  style={{ accentColor: theme.accent, cursor: 'pointer' }}
                />
              </div>
            )}

            {compareMode && (
              <div style={{ position: 'absolute', top: 8, left: 8 }}>
                <input type="checkbox" checked={isComparing} readOnly style={{ accentColor: theme.accent, cursor: 'pointer' }} />
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              marginBottom: viewMode === 'grid' ? 12 : 0,
              width: viewMode === 'list' ? 180 : undefined, flexShrink: 0,
              paddingLeft: 20,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{font.name}</div>
                <div style={{ fontSize: 10, color: theme.textFaint, marginTop: 2 }}>
                  {font.category} · {font.style_count} styles · {font.size_display}
                  {font.is_variable ? ' · Variable' : ''}
                </div>
              </div>
              <button
                onClick={e => { e.stopPropagation(); toggleFavorite(font.id); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: font.is_favorited ? theme.orange : theme.textDisabled,
                  fontSize: 14, padding: 2,
                }}
              >{font.is_favorited ? '★' : '☆'}</button>
            </div>

            <div style={{
              fontFamily: `'${font.family}', serif`,
              fontSize: previewSize, lineHeight: 1.3, color: theme.textSecondary,
              marginBottom: viewMode === 'grid' ? 14 : 0,
              overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: viewMode === 'list' ? 'nowrap' : undefined,
              maxHeight: viewMode === 'grid' ? previewSize * 2.6 : undefined,
              flex: viewMode === 'list' ? 1 : undefined,
              minWidth: viewMode === 'list' ? 0 : undefined,
            }}>{previewText}</div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              width: viewMode === 'list' ? 260 : undefined, flexShrink: 0,
            }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                {font.avg_rating > 0 && (
                  <StarRating avgRating={font.avg_rating} ratingCount={font.rating_count} size={10} />
                )}
                {(font.collections || []).map(c => (
                  <span key={c.name} style={{
                    fontSize: 9, fontWeight: 600, color: theme.badgeText,
                    background: theme.badgeBg,
                    padding: '2px 7px', borderRadius: 4,
                    letterSpacing: '0.03em', textTransform: 'uppercase',
                  }}>{c.name}</span>
                ))}
                {(font.tags || []).map(t => (
                  <span key={t.id} style={{
                    fontSize: 9, fontWeight: 600,
                    color: t.color || theme.accent,
                    background: `${t.color || theme.accent}18`,
                    padding: '2px 7px', borderRadius: 4, letterSpacing: '0.03em',
                  }}>{t.name}</span>
                ))}
                {!!font.is_personal && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: theme.textGhost,
                    background: theme.bgInput, border: `1px solid ${theme.borderLight}`,
                    padding: '1px 6px', borderRadius: 3, letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                  }}>Personal</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {!!font.is_personal && font.uploaded_by === user?.id && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (confirm(`Delete "${font.name}"? This cannot be undone.`)) handleDeleteFont(font.id);
                    }}
                    style={{
                      background: 'none', border: `1px solid ${theme.borderLight}`,
                      borderRadius: 6, color: theme.textFaint,
                      fontSize: 10, fontWeight: 600, padding: '4px 8px',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >✕</button>
                )}
              <button
                onClick={e => { e.stopPropagation(); toggleActivation(font.id).then(r => r?.active_count != null && setActiveCount(r.active_count)); }}
                style={{
                  background: font.is_activated ? theme.greenBg : theme.bgInput,
                  border: font.is_activated ? `1px solid ${theme.greenBorder}` : `1px solid ${theme.borderHover}`,
                  borderRadius: 6,
                  color: font.is_activated ? theme.green : theme.textMuted,
                  fontSize: 10, fontWeight: 600, padding: '4px 12px',
                  cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em',
                  flexShrink: 0,
                }}
              >{font.is_activated ? '✓ Active' : 'Activate'}</button>
              </div>
            </div>
          </div>
          );
        })}

        {!fontsLoading && fonts.length === 0 && (
          <div style={{
            gridColumn: '1 / -1', textAlign: 'center',
            padding: 60, color: theme.textFaint,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}>◈</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No fonts found</div>
            <div style={{ fontSize: 12 }}>
              {searchQuery ? 'Try a different search term' : 'Upload some fonts to get started'}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 8,
          marginTop: 24, paddingBottom: 16,
        }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{
              background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
              borderRadius: 6, color: theme.textTertiary, padding: '6px 12px',
              cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: 12,
              fontFamily: 'inherit', opacity: page <= 1 ? 0.3 : 1,
            }}
          >← Prev</button>
          <span style={{
            fontSize: 12, color: theme.textMuted, display: 'flex', alignItems: 'center',
          }}>
            Page {page} of {pagination.pages}
          </span>
          <button
            disabled={page >= pagination.pages}
            onClick={() => setPage(p => p + 1)}
            style={{
              background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
              borderRadius: 6, color: theme.textTertiary, padding: '6px 12px',
              cursor: page >= pagination.pages ? 'not-allowed' : 'pointer', fontSize: 12,
              fontFamily: 'inherit', opacity: page >= pagination.pages ? 0.3 : 1,
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}
